import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { SkeletonCard } from './components/SkeletonCard'
import { formatFiatCurrency } from '@/utils/currency/formatFiatCurrency'
import { useAppPreferencesStore } from '@/store'
import { useAppAnalytics } from '@/services/analytics'
import { responsiveAdapter } from '@/utils'

interface WalletProps {
  balance: number
  currencyCode?: string
  skeletonActive?: boolean
  containerStyle?: React.CSSProperties
}

interface CryptoActionProps {
  iconName: string
  label: string
  onPress: () => void
}

const CryptoAction: React.FC<CryptoActionProps> = ({
  iconName,
  label,
  onPress,
}) => (
  <BaseView
    center
    py={14}
    gap={8}
    rounded={24}
    onClick={onPress}
    style={{
      width: `${responsiveAdapter(83)}px`,
      backgroundColor: '#5B4AE6',
      cursor: 'pointer',
      transition: 'opacity 150ms ease',
    }}
  >
    <AppIcons name={iconName} size={25} color="label.buttonPrimaryEnabled" />
    <BaseText
      fontSize={13}
      fontFamily="semibold"
      color="label.buttonPrimaryEnabled"
    >
      {label}
    </BaseText>
  </BaseView>
)

export const Wallet: React.FC<WalletProps> = ({
  balance,
  currencyCode = 'USD',
  skeletonActive = false,
  containerStyle,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { displayCurrency } = useAppPreferencesStore()
  const { trackEvent, events } = useAppAnalytics()

  const activeCurrencyCode = displayCurrency?.isin || currencyCode || 'USD'

  const balanceFormatted = formatFiatCurrency({
    value: balance,
    showDecimals: true,
    fiatCode: activeCurrencyCode,
  })

  // Font size snap points matching mobile exactly
  const getBalanceFontSize = (balanceText: string) => {
    const fontSizeSnapPoints = [
      { maxLength: 8, fontSize: 56 },
      { maxLength: 10, fontSize: 46 },
      { maxLength: 13, fontSize: 36 },
      { maxLength: 16, fontSize: 28 },
      { maxLength: 21, fontSize: 24 },
      { maxLength: Infinity, fontSize: 18 },
    ]

    return (
      fontSizeSnapPoints.find(
        ({ maxLength }) => balanceText.length <= maxLength,
      )?.fontSize ?? 18
    )
  }
  const balanceFontSize = getBalanceFontSize(balanceFormatted)

  if (skeletonActive) {
    return <SkeletonCard />
  }

  const handlePortfolioPress = () => {
    navigate('/portfolio')
  }

  const handleAddMoneyPress = () => {
    trackEvent(events.HOME_CTA_CLICKED, { action_name: 'add_money' })
    navigate('/add-money/select-deposit-type')
  }

  const handleConvertPress = () => {
    trackEvent(events.HOME_CTA_CLICKED, { action_name: 'convert' })
    navigate('/conversion', {
      state: { debitCurrencyCode: 'USDT', creditCurrencyCode: 'BTC' },
    })
  }

  const handleSendPress = () => {
    trackEvent(events.HOME_CTA_CLICKED, { action_name: 'send' })
    navigate('/send-request', {
      state: { type: 'send', cryptoCode: 'USDT' },
    })
  }

  const handleRequestPress = () => {
    trackEvent(events.HOME_CTA_CLICKED, { action_name: 'request' })
    navigate('/send-request', {
      state: { type: 'request', cryptoCode: 'USDT' },
    })
  }

  return (
    <BaseView gap={14} style={containerStyle}>
      {/* Balance Section - matching mobile: flat, no card wrapper */}
      <BaseView
        align="center"
        self="center"
        gap={8}
        pt={13}
        onClick={handlePortfolioPress}
        style={{ cursor: 'pointer' }}
      >
        <BaseText
          fontSize={14}
          fontFamily="regular"
          color="label.description"
          textAlign="center"
        >
          {t('common:totalBalance')}
        </BaseText>

        <BaseText
          fontSize={balanceFontSize}
          fontFamily="medium"
          color="label.default"
          textAlign="center"
          style={{
            lineHeight: `${balanceFontSize + 4}px`,
          }}
        >
          {balanceFormatted}
        </BaseText>
      </BaseView>

      {/* Action Buttons - vertical layout matching mobile */}
      <BaseView
        row
        align="center"
        justify="space-between"
        gap={4}
        px={12}
        pt={19}
      >
        <CryptoAction
          iconName="plus"
          label={t('transactions:types.deposit')}
          onPress={handleAddMoneyPress}
        />
        <CryptoAction
          iconName="exchange"
          label={t('form:actions.convert')}
          onPress={handleConvertPress}
        />
        <CryptoAction
          iconName="upload"
          label={t('form:actions.send')}
          onPress={handleSendPress}
        />
        <CryptoAction
          iconName="arrowLeft"
          label={t('form:actions.request')}
          onPress={handleRequestPress}
        />
      </BaseView>
    </BaseView>
  )
}
